import React, { ReactNode, useEffect, useRef } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { FlexBox } from '@lumapps/lumx/react';

import './index.scss';

interface ChatMessageGroupProps {
    // The messages to display
    children: ReactNode;
    /** Classname to add to the group */
    className?: string;
    /**
     * Whether the group message must scroll into view when it appears
     * Can either be a boolean to enable scroll or an object of options to override default ones
     */
    scrollIntoView?: boolean | ScrollIntoViewOptions;
}

const CLASSNAME = 'lumx-chat-message-group';

/**
 * Group multiple messages together.
 * This can be useful if you want to apply specific styling or animation to new "groups"
 * @family Chat
 */
export const ChatMessageGroup: React.FC<ChatMessageGroupProps> = ({ children, className, scrollIntoView = false }) => {
    const { block } = useClassnames(CLASSNAME);

    const groupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const messageEl = groupRef.current;
        if (messageEl && scrollIntoView) {
            const defaultOptions: ScrollIntoViewOptions = {
                block: 'start',
                inline: 'center',
                behavior: 'smooth',
            };
            const options =
                typeof scrollIntoView === 'object' ? { ...defaultOptions, ...scrollIntoView } : defaultOptions;
            messageEl.scrollIntoView(options);
        }
    }, [scrollIntoView]);

    return (
        <FlexBox ref={groupRef} className={block([className])} orientation="vertical" gap="big">
            {children}
        </FlexBox>
    );
};
